import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AppConfig } from 'src/app/app-config.loader';
import { PosizioniEventType } from 'src/app/shared/components/add-position/add-position.component';
import { Posizione, POSIZIONE_ROOT } from 'src/app/shared/models/posizione.model';
import { PosizioniService } from '../../core/posizioni.service';
import { GestionePosizioniItem } from './gestione-posizioni.model';

@Component({
  selector: 'app-gestione-posizioni',
  templateUrl: './gestione-posizioni.component.html',
  styleUrls: ['./gestione-posizioni.component.scss']
})
export class GestionePosizioniComponent implements OnInit {
  public tree$: Observable<Posizione[]> | undefined;
  public addDialogVisible = false;
  public nodeToModify: GestionePosizioniItem | null = null;
  public typeOfEvent: PosizioniEventType = 'add';
  public today = new Date();
  public expandSearchedRows = false;
  public isMoveDialogActive = false;
  public disableFromLevel = -1;
  public nodeFrom: string | null = null;
  public isFascicoloDefault = false;
  public defaultAddLabel: string;
  public posizioneRoot = POSIZIONE_ROOT;

  constructor(private readonly appConfig: AppConfig, private readonly posizioniService: PosizioniService, private readonly messageService: MessageService) {
    this.defaultAddLabel = this.appConfig.logic.hierarchy.labels[0].toLowerCase();
  }

  ngOnInit(): void {
    this.tree$ = this.posizioniService.getTree().pipe(
      map(res => this.setTree(res))
    );
  }

  openModalAddFolder() {
    this.typeOfEvent = 'add';
    this.nodeToModify = null;
    this.addDialogVisible = true;
  }

  closeModalAddFolder(): void {
    this.addDialogVisible = false;
  }

  modifyNode(event: { payload: GestionePosizioniItem, type: PosizioniEventType }): void {
    this.typeOfEvent = event.type;
    this.nodeToModify = event.payload;
    this.addDialogVisible = true;
  }

  moveNode(event: { payload: GestionePosizioniItem }): void {
    this.nodeFrom = event.payload.posizioneId;
    this.disableFromLevel = event.payload.level;
    this.isMoveDialogActive = true;
  }

  downloadAggregazioneXml(event: { payload: GestionePosizioniItem }): void {
    this.posizioniService.downloadAggregazioneXml(event.payload.posizioneId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Export XML',
          detail: 'Download XML avviato correttamente'
        });
      },
      error: error => {
        this.handleAggregazioneXmlError(error);
      }
    });
  }

  closeMoveNode(): void {
    this.isMoveDialogActive = false;
  }

  saveMoveNode(nodeTo: string): void {
    if (this.nodeFrom === null) return;

    this.posizioniService.moveNode(this.nodeFrom, nodeTo).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Esito operazione',
          detail: 'Elemento spostato correttamente'
        });
        this.ngOnInit();
        this.closeMoveNode();
      },
      error: err => {
        console.error(err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Elemento non spostato'
        });
      }
    });
  }

  addedPosition() {
    this.addDialogVisible = false;
    this.ngOnInit();
  }

  private handleAggregazioneXmlError(error: HttpErrorResponse): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Errore export XML',
      detail: this.resolveAggregazioneXmlErrorDetail(error)
    });
  }

  private setTree(tree: Posizione[]): Posizione[] {
    this.removeRoot(tree);
    // this.setBreadcrumb(tree);
    return tree;
  }

  private removeRoot(tree: Posizione[]): void {
    if (tree[0].posizioneId.length === 1) {
      tree.splice(0, 1);
    }
  }

  private resolveAggregazioneXmlErrorDetail(error: HttpErrorResponse): string {
    if (error.status === 400) {
      const payload = error.error;
      if (Array.isArray(payload)) {
        return payload.join(' ');
      }

      if (typeof payload === 'string' && payload.trim().length > 0) {
        return payload;
      }
    }

    if (error.status === 404) {
      return 'Fascicolo non trovato';
    }

    return 'Impossibile generare l\'XML del fascicolo';
  }

  private setBreadcrumb(tree: Posizione[]): void {
    tree.forEach((node: Posizione) => {
      switch (node.posizioneId.split('/').length) {
        case 3:
          this.addFascicolo(node);
          break;
        case 4:
          this.addSottfasc(tree, node);
          break;
        case 5:
          this.addServInt(tree, node);
          break;
        default:
          break;
      }
    });
  }

  private searchFold(tree: Posizione[], folderToFind: string): Posizione | undefined {
    return tree.find(findNode => folderToFind === findNode.posizioneId);
  }

  private addFascicolo(node: Posizione): Posizione {
    node.title = ``;
    return node;
  }

  private addSottfasc(tree: Posizione[], node: Posizione): Posizione {
    let fascToSearch = node.posizioneId.split('/')[1];
    fascToSearch = `/${fascToSearch}/`;
    const fascSearched = this.searchFold(tree, fascToSearch);
    if (fascSearched) node.title = `Fascicolo: ${fascSearched.sigla}`;
    return node;
  }

  private addServInt(tree: Posizione[], node: Posizione): Posizione {
    const elToSearch = node.posizioneId.split('/');
    const fascToSearch = `/${elToSearch[1]}/`;
    const sottfascToSearch = `/${elToSearch[1]}/${elToSearch[2]}/`;
    const fascSearched = this.searchFold(tree, fascToSearch);
    const sottfascSearched = this.searchFold(tree, sottfascToSearch);
    if (fascSearched && sottfascSearched) node.title = `Fascicolo: ${fascSearched.sigla}, Sottofascicolo: ${sottfascSearched.sigla}`;
    return node;
  }
}
